/**
 * Reading something from Nexus, with every state it can actually be in.
 *
 * A screen in a governed platform has more states than "data" and "spinner".
 * It can be forbidden, which is not an error to retry. It can be empty, which
 * is not a failure. It can be refused by policy while the rest of the page is
 * fine. Collapsing those into `loading | data | error` is how a UI ends up
 * telling somebody "something went wrong" when the truthful answer was "you are
 * not allowed to see this".
 *
 * So the state is a discriminated union and the components that render it are
 * exhaustive.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { NexusError, TransportError } from "./errors";

export type Resource<T> =
  | { kind: "loading" }
  | { kind: "ready"; value: T }
  | { kind: "forbidden"; error: NexusError }
  | { kind: "missing"; error: NexusError }
  | { kind: "failed"; error: NexusError | TransportError };

export interface Reader<T> {
  state: Resource<T>;
  /** Read again — after a mutation, or because somebody pressed refresh. */
  reload: () => void;
}

function classify<T>(error: unknown): Resource<T> {
  if (error instanceof NexusError) {
    if (error.isForbidden) return { kind: "forbidden", error };
    if (error.isNotFound) return { kind: "missing", error };
    return { kind: "failed", error };
  }
  if (error instanceof TransportError) return { kind: "failed", error };
  return {
    kind: "failed",
    error: new TransportError(error instanceof Error ? error.message : "Something went wrong."),
  };
}

/**
 * `read` must be stable — wrap it in `useCallback` — because it is the identity
 * of the request. `enabled: false` holds the resource in `loading` without
 * asking, which is what a screen wants while it is still deciding what to ask.
 */
export function useResource<T>(
  read: (signal: AbortSignal) => Promise<T>,
  options: { enabled?: boolean } = {},
): Reader<T> {
  const enabled = options.enabled ?? true;
  const [nonce, setNonce] = useState(0);

  // One object identity per request. Anything that changes what is being asked
  // changes the token, and a result carries the token it answered — so a
  // superseded answer is ignored without the effect having to reset state
  // synchronously and cascade a render.
  const token = useMemo(() => ({ read, enabled, nonce }), [read, enabled, nonce]);
  const [answer, setAnswer] = useState<{ token: object; state: Resource<T> } | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    read(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setAnswer({ token, state: { kind: 'ready', value } });
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        if (error instanceof DOMException && error.name === 'AbortError') return;
        setAnswer({ token, state: classify<T>(error) });
      });
    return () => {
      controller.abort();
    };
  }, [token, read, enabled]);

  const reload = useCallback(() => {
    setNonce((value) => value + 1);
  }, []);

  const state: Resource<T> =
    answer !== null && answer.token === token ? answer.state : { kind: 'loading' };

  return { state, reload };
}
